import { NextSeo } from 'next-seo'
import React from 'react'
import { jsonLdScriptProps } from 'react-schemaorg'
import { ItemList } from 'schema-dts'
import { getProfileIndexFromLanguage } from '../getProfile'
import { getCanonicalUrl } from './getCanonicalUrl'

interface CategoryMetaTagsProps {
  currentCategoryData: Record<string, any>
  locale: string
  locales: string[]
  shopSettingsData: Record<string, any>
  productsCount: number
  pageNumber: number
  categorySlug: string
  defaultLocale: string
  initialI18nStore: Record<string, any>
  productSlug: string
  productContainers: any
}

export interface languageAlternate {
  hrefLang: string
  href: string
}

export const categoryMetaTags = ({
  currentCategoryData,
  locale,
  locales,
  shopSettingsData,
  productsCount,
  pageNumber,
  categorySlug,
  defaultLocale,
  initialI18nStore,
  productSlug,
  productContainers,
}: CategoryMetaTagsProps) => {
  const shopProfileIndex = getProfileIndexFromLanguage(
    shopSettingsData.data.findFirstShopSetting?.shop_profiles,
    locale,
  )
  //* ============================================================
  //* ====================== PROFILES ============================
  //* ============================================================
  //!if the index passed to the profiles array is null, the profile will be undefined
  const categoryProfile =
    currentCategoryData.data.findUniqueMetaInformation.category_profile
  const shopProfile =
    shopSettingsData.data.findFirstShopSetting?.shop_profiles[shopProfileIndex]

  // ========================
  // ====== CONSTANTS =======
  // ========================
  const shopSettings = shopSettingsData.data.findFirstShopSetting
  const shopName = shopProfile?.name ?? 'Furnisystems'
  const permalink = currentCategoryData.data.findUniqueMetaInformation.permalink
  const numberOfProductsInCategory = productsCount
  const image =
    categoryProfile?.category.image?.src ??
    'No image found, please add it in the settings'
  const products = categoryProfile.category.product_containers
  const currency = shopSettings?.currency ?? 'EUR'

  //* ========================================
  //* ============canonical url===============
  //* ========================================
  //here we should get the url we want to refer to
  //if the current locale is /lv we want google to refer to the .lv domain
  //we do so by passing the locale to the "getCanonicalUrl" (which should be kept up to date with the Language schema enum)
  const domainToRefer = getCanonicalUrl(locale, shopSettings?.enabled_languages)
  const currentDomain = getCanonicalUrl(
    defaultLocale,
    shopSettings?.enabled_languages,
  )

  const canonicalUrl = `${domainToRefer}/${categorySlug}/${permalink}/${pageNumber}`
  // const url = `${currentDomain}/${locale}/${categorySlug}/${permalink}/${pageNumber}`
  const url = `${currentDomain}/${categorySlug}/${permalink}/${pageNumber}`

  //* ========================================================
  //* ================== TITLE CONSTRUCTOR ===================
  //* ========================================================
  //*The fallback title will be formatted like so:
  //* <category name> | <shop name>
  //SOME PARAMS MIGHT RESULT IN UNDEFINED DUE TO THE PROFILE NOT BEING FOUND
  //IT'S NOT A PROBLEM, THE CATEGORY WON'T BE DISPLAYED IF IT DOESN'T HAVE A PROFILE
  const titleParams = {
    firstParam: categoryProfile?.name,
    secondParam: shopName,
  }

  let titleTemplate = ''
  // //* ==============================================================
  //   //* ================== DESCRIPTION CONSTRUCTOR ===================
  //   //* ==============================================================

  const descriptionParams = {
    firstParam: shopName,
    secondParam: numberOfProductsInCategory,
    thirdParam: categoryProfile?.name,
  }
  let descriptionTemplate = ''

  //* =================================
  //* =========== TEMPLATES CATEGORY ===========
  //* =================================
  switch (locale) {
    case 'lt':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} produktai `

      //set description
      descriptionTemplate = `Pirkite stilingai su ${descriptionParams.firstParam} gražioje ${descriptionParams.thirdParam} baldų kategorijoje su ${descriptionParams.secondParam} produktų.`
      break
    case 'et':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} tooted `
      //set description
      descriptionTemplate = `Ostke stiilselt koos ${descriptionParams.firstParam} ilusas ${descriptionParams.thirdParam} mööblikollektsioon koos ${descriptionParams.secondParam} tooted.`
      break
    case 'lv':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} produktiem `

      //set description
      descriptionTemplate = `Pērciet stilīgi ar ${descriptionParams.firstParam} skaistajā ${descriptionParams.thirdParam} mēbeļu kolekcija ar ${descriptionParams.secondParam} produktiem.`
      break
    case 'en':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${numberOfProductsInCategory} products | ${titleParams.secondParam}`

      //set description
      descriptionTemplate = `Shop in style on ${descriptionParams.firstParam} with our beautiful collection of ${descriptionParams.secondParam} ${descriptionParams.thirdParam}`
      break
    case 'ru':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} продукты`

      //set description
      descriptionTemplate = `Купить стильно с ${descriptionParams.firstParam} в прекрасном ${descriptionParams.thirdParam} коллекция мебели с ${descriptionParams.secondParam} продукты.`
      break
    case 'de':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} Produkte `

      //set description
      descriptionTemplate = `Shoppen Sie stilvoll mit ${descriptionParams.firstParam} in einer wunderschönen ${descriptionParams.thirdParam} in der Kategorie Möbel mit ${descriptionParams.secondParam} Produkten.`
      break
    case 'dk':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} Produkter `

      //set description
      descriptionTemplate = `Shop i stil med ${descriptionParams.firstParam} i en smuk ${descriptionParams.thirdParam} i møbelkategorien med ${descriptionParams.secondParam} produkter.`
      break
    case 'es':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} productos `

      //set description
      descriptionTemplate = `Compra con estilo con ${descriptionParams.firstParam} en un hermoso ${descriptionParams.thirdParam} en la categoría de muebles con ${descriptionParams.secondParam} productos.`
      break
    case 'fr':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} des produits `

      //set description
      descriptionTemplate = `Magasinez avec style avec ${descriptionParams.firstParam} dans un beau ${descriptionParams.thirdParam} dans la catégorie meuble avec ${descriptionParams.secondParam} produits.`
      break
    case 'it':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} prodotti `

      //set description
      descriptionTemplate = `Acquista con stile con ${descriptionParams.firstParam} in un bellissimo ${descriptionParams.thirdParam} nella categoria mobili con ${descriptionParams.secondParam} prodotti.`
      break
    case 'nl':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} producten `

      //set description
      descriptionTemplate = `Winkel in stijl met ${descriptionParams.firstParam} in een mooie ${descriptionParams.thirdParam} in de categorie meubelen met ${descriptionParams.secondParam} producten.`
      break
    case 'pl':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} produkty `

      //set description
      descriptionTemplate = `Kupuj stylowo z ${descriptionParams.firstParam} w pięknym ${descriptionParams.thirdParam} w kategorii meble z ${descriptionParams.secondParam} produktami.`
      break
    case 'pt':
      //set title
      titleTemplate = `${titleParams.firstParam} | ${titleParams.secondParam} | ${numberOfProductsInCategory} produtos `

      //set description
      descriptionTemplate = `Compre com estilo com ${descriptionParams.firstParam} em um lindo ${descriptionParams.thirdParam} na categoria móveis com ${descriptionParams.secondParam} produtos.`
      break
    default:
      console.log(
        'ADD THE NEW LOCALE IN THE METATAGS UTILITY! We hit the default case in the creation of the category template for meta_description. This could mean that you forgot to add a new locale to the switch',
      )
      //set title
      titleTemplate = `${titleParams.firstParam} | ${numberOfProductsInCategory} products | ${titleParams.secondParam}`

      //set description
      descriptionTemplate = `Shop in style on ${descriptionParams.firstParam} with our beautiful collection of ${descriptionParams.secondParam} ${descriptionParams.thirdParam}`

      break
  }
  // Each variation of the page should include a set of <link> elements in the <head> element
  // one link for each page variant including itself. The set of links is identical for every version of the page
  // - (https://developers.google.com/search/docs/advanced/crawling/localized-versions)

  // TODO INVESIGATE
  // DOESNT WORK CORRECTLY
  // Produced oyutput <link rel="alternate" hrefLang="lt" href="https://www.furnibay.lt/lt/kategorija/1"/>;

  let languageAlternates: languageAlternate[] = []
  // for (let locale of locales) {
  //   for (let key in initialI18nStore) {
  //     if (key === locale) {
  //       languageAlternates.push({
  //         hrefLang: locale,
  //         href: `${currentDomain}/${locale}/${initialI18nStore[locale].common['category-slug']}/${pageNumber}`,
  //       })
  //     }
  //   }
  // }

  //* ================================================
  //* ============== END OF PIPELINE =================
  //* ================================================
  let title =
    currentCategoryData.data.findUniqueMetaInformation.meta_title === '' ||
      currentCategoryData.data.findUniqueMetaInformation.meta_title === null
      ? titleTemplate
      : currentCategoryData.data.findUniqueMetaInformation.meta_title
  let description =
    currentCategoryData.data.findUniqueMetaInformation.meta_description === '' ||
      currentCategoryData.data.findUniqueMetaInformation.meta_description === null
      ? categoryProfile?.short_description ?? descriptionTemplate
      : currentCategoryData.data.findUniqueMetaInformation.meta_description

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        languageAlternates={languageAlternates}
        openGraph={{
          url,
          title,
          description,
          images: [{ url: image }],
          site_name: shopName,
          type: 'product.group',
        }}
      // twitter={{
      //   handle: '@furnibay',
      //   site: '@furnibay',
      //   cardType: 'summary_large_image',
      // }}
      />
      <script
        {...jsonLdScriptProps<ItemList>({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          url: url,
          numberOfItems: numberOfProductsInCategory,
          itemListElement: [
            ...productContainers.map((product) => {
              let productImage
              let productURL
              let productName
              let productPrice

              let productSameAs = product.primary_category
                ? product.primary_category.schema_href
                : product.categories?.length > 0
                  ? product.categories[0].schema_href
                  : null

              if (product.type === 'SIMPLE_PRODUCT') {
                // ---- SIMPLE PRODUCT CASE

                const productProfileIndex: number = getProfileIndexFromLanguage(
                  product.single_product.product_profiles,
                  locale,
                )
                if (
                  !product.single_product.product_profiles[productProfileIndex]
                ) {
                  return null
                }

                // ---- EACH Product will have a primary category or a categories list
                // --- This list will include schema_href variable which is a link to a wikiData entity page.
                // --- to be used for the sameAS property

                // Edit saving bug in DB... (some reason urls are saved without https...)
                if (productSameAs) {
                  productSameAs = productSameAs.replace('//www', 'https://www')
                }

                productImage = product.single_product.images[0]
                productURL =
                  `${currentDomain}/${locale}/${product}/${product.single_product.product_profiles[productProfileIndex].meta_information.permalink}` ??
                  ''
                productName =
                  product.single_product.product_profiles[productProfileIndex]
                    .name
                productPrice = product.single_product.price
              } else if (product.type === 'ADVANCED_PRODUCT') {
                // --- ADVANCED PRODUCT CASE

                const productProfileIndex: number = getProfileIndexFromLanguage(
                  product.advanced_product.advanced_product_profiles,
                  locale,
                )
                if (
                  !product.advanced_product.advanced_product_profiles[
                  productProfileIndex
                  ]
                ) {
                  return null
                }

                // ---- EACH Product will have a primary category or a categories list
                // --- This list will include schema_href variable which is a link to a wikiData entity page.
                // --- to be used for the sameAS property
                // We use primary category, if it doesnt exist we fall back to one of the categories

                // Edit saving bug in DB... (some reason urls are saved without https...)
                if (productSameAs) {
                  productSameAs = productSameAs.replace('//www', 'https://www')
                }

                productImage = product.advanced_product.images[0]
                productURL =
                  `${currentDomain}/${locale}/${product}/${product.advanced_product.advanced_product_profiles[productProfileIndex].meta_information.permalink}` ??
                  ''
                productName =
                  product.advanced_product.advanced_product_profiles[
                    productProfileIndex
                  ].name
                productPrice = product.advanced_product.price_from
              } else {
                // --- Unhandled product type
                return null
              }

              return {
                '@type': 'Product',
                image: productImage,
                url: productURL,
                name: productName,
                sameAs: productSameAs, // To reference the itemType
                offers: {
                  '@type': 'Offer',
                  price: productPrice,
                  priceCurrency: currency,
                },
              }
            }),
          ],
        })}
      />
    </>
  )
}
